import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  PhoneIncoming,
  PhoneOutgoing,
  User,
} from 'lucide-react';
import type {
  AdminActionItem,
  AdminCallDetail,
  AdminCallEngineProcessing,
  AdminCallTranscriptResponse,
  AdminCoachingNotes,
  TranscriptSegment,
} from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';
import { useSessionStore } from '../stores/session';
import { formatDuration } from '../lib/time-format';
import { OwnerResolutionLabel } from '../lib/owner-resolution-label';
import { actionItemToggleMessage } from '../lib/action-flow-feedback';
import { buildCallDetailStateCards, type CallDetailStateCardTone } from '../lib/call-detail-state';
import { buildEngineProcessingDisplay } from '../lib/engine-processing-display';
import { buildContactDetailPath } from '../lib/contact-paths';
import { PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';

// One call's real, server-owned record.
//
// Phase 2 surfaced: routing, owner, recording URL, summary/desktop output/outcome.
// Phase 3 adds:    sentiment badge (header) and a lazy-loaded transcript
// section (fetches GET /api/admin/calls/:sid/transcript only when the
// user expands it — transcripts can be ~1 MB).
//
// Honesty policy (unchanged): desktop-processed fields are surfaced
// only when present. Null is null; we don't synthesize. The raw
// 7-dimension desktop rubric is stored on the server but deliberately
// NOT rendered here — see docs/PHASE-3.md.

export default function CallDetail() {
  const { sid } = useParams<{ sid: string }>();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const [data, setData] = useState<AdminCallDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!sid || !activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<AdminCallDetail>(
        `/api/admin/calls/${encodeURIComponent(sid)}`,
      );
      setData(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Call not found in this organization.');
      } else {
        setError(
          err instanceof ApiError ? err.message : (err as Error).message || 'Failed to load call',
        );
      }
    } finally {
      setLoading(false);
    }
  }, [sid, activeOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="max-w-6xl space-y-5">
      {loading ? (
        <CallLoadingState />
      ) : error ? (
        <CallErrorState error={error} />
      ) : data ? (
        <CallBody data={data} />
      ) : null}
    </div>
  );
}

function CallLoadingState() {
  return (
    <>
      <PageHeader
        eyebrow="Source detail"
        title="Loading call…"
        description="Fetching the call record. Transcript content still loads only when opened."
        action={<BackToCallsButton />}
      />
      <SectionCard className="px-5 py-12 text-center">
        <div className="mx-auto h-10 w-10 animate-pulse rounded-2xl bg-blue-50" />
        <p className="mt-3 text-sm font-medium text-slate-900">Loading call detail</p>
        <p className="mt-1 text-xs text-slate-500">Routing, recording, action item, coaching, and transcript availability will appear here.</p>
      </SectionCard>
    </>
  );
}

function CallErrorState({ error }: { error: string }) {
  return (
    <>
      <PageHeader
        eyebrow="Source detail"
        title="Call detail unavailable"
        description="The detail route is intact, but the server could not return this call record."
        action={<BackToCallsButton />}
      />
      <SectionCard className="border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        {error}
      </SectionCard>
    </>
  );
}

function BackToCallsButton() {
  return (
    <Link
      to="/calls"
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm shadow-slate-200/70 transition hover:border-blue-200 hover:text-blue-700"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to calls
    </Link>
  );
}

function SectionHeading({
  title,
  kicker,
  badge,
}: {
  title: string;
  kicker?: string;
  badge?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</h2>
        {kicker && <p className="mt-1 text-sm text-slate-600">{kicker}</p>}
      </div>
      {badge}
    </div>
  );
}

function FactTile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-1 min-w-0 break-words text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}

function CompactFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-100 bg-white/80 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      <div className="mt-1 truncate text-sm font-medium text-slate-800" title={typeof value === 'string' ? value : undefined}>
        {value}
      </div>
    </div>
  );
}

function CallBody({ data }: { data: AdminCallDetail }) {
  const { call, recording } = data;
  const isIn = call.direction === 'inbound';
  const stateCards = buildCallDetailStateCards({
    hasRecording: call.hasRecording,
    hasRecordingMetadata: Boolean(recording),
    hasEngineSummary: Boolean(call.hasEngineSummary),
    hasTranscript: data.hasTranscript,
    hasCoaching: Boolean(data.coaching),
  });
  // The "contact" is the external party — the number we DIDN'T own.
  // For inbound, that's `from`; for outbound, `to`. Matches the

  const contactPhone = isIn ? call.from : call.to;

  const openActionItems = data.actionItems.filter(item => !item.completed).length;
  const directionLabel = isIn ? 'Inbound call' : 'Outbound call';
  const recordingStatus = !call.hasRecording
    ? 'Not captured'
    : recording
      ? 'Captured'
      : 'Waiting';
  const transcriptStatus = data.hasTranscript ? 'Available' : 'Not uploaded';
  const coachingStatus = data.coaching ? 'Available' : 'Not uploaded';

  return (
    <>
      <PageHeader
        eyebrow="Source detail"
        title="Call detail"
        description={(
          <span className="flex flex-wrap items-center gap-2">
            <span>{new Date(call.startTime).toLocaleString()}</span>
            <span aria-hidden="true">•</span>
            <span>{formatDuration(call.duration)}</span>
            <span aria-hidden="true">•</span>
            <span className="font-mono text-xs text-slate-500">SID {shortSid(call.sid)}</span>
          </span>
        )}
        action={(
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <BackToCallsButton />
            <Link
              to={buildContactDetailPath(contactPhone)}
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 shadow-sm shadow-slate-200/70 transition hover:border-blue-300 hover:bg-blue-100"
            >
              Contact record
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      />

      <SectionCard className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${isIn ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
              {isIn ? <PhoneIncoming className="h-5 w-5" /> : <PhoneOutgoing className="h-5 w-5" />}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={isIn ? 'emerald' : 'blue'} size="sm">{directionLabel}</StatusBadge>
                <StatusBadge tone="slate" size="sm">{call.status}</StatusBadge>
                {data.ownerResolution ? <StatusBadge tone="blue" size="sm">Owner resolved</StatusBadge> : <StatusBadge tone="amber" size="sm">Owner unavailable</StatusBadge>}
              </div>
              <div className="mt-3 break-words font-mono text-base font-semibold text-slate-950">
                <PhoneLink phone={call.from} linkThis={isIn} />
                <span className="mx-2 text-slate-300">→</span>
                <PhoneLink phone={call.to} linkThis={!isIn} />
              </div>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                Optional source view for facts the server already has. Transcript content still loads only when opened.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <CompactFact label="Duration" value={formatDuration(call.duration)} />
          <CompactFact label="Status" value={call.status} />
          <CompactFact label="Recording" value={recordingStatus} />
          <CompactFact label="Transcript" value={transcriptStatus} />
          <CompactFact label="Coaching" value={coachingStatus} />
          <CompactFact label="Action items" value={data.actionItems.length > 0 ? `${openActionItems} open` : 'None'} />
        </div>
      </SectionCard>

      {/* ─── Routing / ownership ─── */}
      <SectionCard className="p-5">
        <SectionHeading
          title="Routing"
          kicker="Business line, external contact, and owner resolution from the call record."
          badge={<StatusBadge tone={data.ownerResolution ? 'blue' : 'amber'} size="sm">{data.ownerResolution ? 'Owner resolved' : 'Needs number mapping'}</StatusBadge>}
        />
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <FactTile label="Direction" value={isIn ? 'Inbound' : 'Outbound'} />
          <FactTile label="External contact" value={<Link to={buildContactDetailPath(contactPhone)} className="font-mono text-blue-700 hover:underline">{contactPhone}</Link>} />
          <FactTile label="Business number" value={<span className="font-mono">{isIn ? call.to : call.from}</span>} />
          <FactTile label="Twilio SID" value={<span className="font-mono text-xs break-all">{call.sid}</span>} />
        </div>
        <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Team owner</div>
          <div className="mt-2 text-sm text-slate-900">
            {data.ownerResolution ? (
              <span className="inline-flex items-center gap-2">
                <User className="h-4 w-4 text-slate-400" />
                <OwnerResolutionLabel owner={data.ownerResolution} />
              </span>
            ) : (
              <span className="text-slate-600">
                No staff assigned to {call.to}. Assign on the{' '}
                <Link to="/numbers" className="font-medium text-blue-700 hover:underline">
                  Numbers page
                </Link>
                .
              </span>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard className="p-5">
        <SectionHeading
          title="Source state"
          kicker="Compact availability from Twilio and Local Engine processing."
          badge={<StatusBadge tone="slate" size="sm">Server-owned facts only</StatusBadge>}
        />
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {stateCards.map(card => (
            <div
              key={card.key}
              className={`rounded-xl border px-3 py-2 ${stateCardClass(card.tone)}`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                {card.label}
              </div>
              <p className="mt-1 text-xs leading-5">{card.detail}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {data.engineProcessing && <EngineProcessingSection engine={data.engineProcessing} />}

      {/* ─── Recording ─── */}
      <SectionCard className="p-5">
        <SectionHeading
          title="Recording"
          kicker="Captured audio metadata. Summaries live in the Local Engine section when present."
          badge={<StatusBadge tone={recording ? 'blue' : 'slate'} size="sm">{recordingStatus}</StatusBadge>}
        />
        {!call.hasRecording ? (
          <p className="mt-4 text-sm text-slate-500">
            No recording was captured for this call.
          </p>
        ) : !recording ? (
          <p className="mt-4 text-sm text-slate-500">
            Recording metadata not yet available. Twilio usually posts it a few seconds after the call ends.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <FactTile label="Recording duration" value={formatDuration(recording.duration)} />
              <FactTile label="Recording status" value={recording.status} />
              <FactTile label="Captured" value={new Date(recording.createdAt).toLocaleString()} />
            </div>

            {/*
              Twilio recording URLs are protected by Twilio's own account
              auth, so linking out means the admin must log in to
              Twilio. We surface the URL rather than streaming through
              our server — streaming would require signed-URL proxying,
              which is its own feature, not Phase 2.
            */}
            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4 text-xs">
              <div className="font-semibold uppercase tracking-[0.14em] text-slate-500">Twilio recording URL</div>
              <a
                href={recording.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 block break-all text-blue-700 hover:underline"
              >
                {recording.url}
              </a>
            </div>
          </div>
        )}
      </SectionCard>

      {/* ─── Action items (Phase 3B) ─── */}
      {/* Shown only when the server has at least one action item. An
          otherwise quiet call is the common case; a placeholder
          "No action items" card would just add visual noise. */}
      {data.actionItems.length > 0 && (
        <ActionItemsSection initial={data.actionItems} />
      )}

      {/* ─── Coaching (Phase 3F) ─── */}
      {/* Shown only when coaching notes exist. Same "no
          empty-state card" rule as action items — a summarized call
          without coaching is common and doesn't need a placeholder. */}
      {data.coaching && <CoachingSection notes={data.coaching} />}

      {/* ─── Transcript (Phase 3, lazy-loaded) ─── */}
      {data.hasTranscript && <TranscriptSection callSid={call.sid} />}
    </>
  );
}
function EngineProcessingSection({ engine }: { engine: AdminCallEngineProcessing }) {
  const display = buildEngineProcessingDisplay(engine);

  return (
    <SectionCard className="p-5">
      <SectionHeading
        title="Local Engine summary"
        kicker="Safe structured output from the paired local Engine. Raw transcript text and runtime details stay hidden."
        badge={<StatusBadge tone={display.tone} size="sm">{display.badge}</StatusBadge>}
      />
      <p className="mt-3 text-sm text-slate-600">{display.message}</p>
      {display.sections.length > 0 && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {display.sections.map(section => (
            <div key={section.title} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{section.title}</div>
              {section.items.length === 1 ? (
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{section.items[0]}</p>
              ) : (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-800">
                  {section.items.map((item, index) => <li key={index}>{item}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/**
 * Action items for a call. Text-only; the sole mutation is the
 * completed checkbox. We optimistically flip the local state on
 * click and reconcile with the server response; if the PATCH 4xx's
 * we restore the previous value and surface a short error.
 *
 * Deliberately NOT in this phase:
 *   - Edit text (desktop LLM is the producer)
 *   - Delete / add (same reason)
 *   - Due dates, assignees (server schema supports neither yet)
 */
function ActionItemsSection({ initial }: { initial: AdminActionItem[] }) {
  const [items, setItems] = useState<AdminActionItem[]>(initial);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function toggle(item: AdminActionItem) {
    const nextCompleted = !item.completed;
    // Optimistic flip so the checkbox feels responsive.
    setItems(prev =>
      prev.map(it => (it.id === item.id ? { ...it, completed: nextCompleted } : it)),
    );
    setError(null);
    setSuccess(null);
    try {
      const updated = await apiFetch<AdminActionItem>(
        `/api/admin/action-items/${item.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed: nextCompleted }),
        },
      );
      setItems(prev => prev.map(it => (it.id === updated.id ? updated : it)));
      setSuccess(actionItemToggleMessage(updated.text, updated.completed));
    } catch (err) {
      // Roll back.
      setItems(prev =>
        prev.map(it => (it.id === item.id ? { ...it, completed: item.completed } : it)),
      );
      setError(
        err instanceof ApiError ? err.message : (err as Error).message || 'Failed to update',
      );
    }
  }

  return (
    <SectionCard className="mt-5 p-5">
      <SectionHeading
        title="Action items"
        kicker="Extracted from the transcript by the Local Engine. Toggling here syncs to the server."
        badge={<StatusBadge tone={items.some(i => !i.completed) ? 'amber' : 'emerald'}>{items.filter(i => !i.completed).length} open</StatusBadge>}
      />
      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          {success}
        </div>
      )}
      <ul className="mt-3 divide-y divide-slate-100">
        {items.map(item => (
          <li key={item.id} className="flex items-start gap-3 py-2">
            <input
              type="checkbox"
              checked={item.completed}
              onChange={() => toggle(item)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span
              className={`text-sm ${
                item.completed ? 'text-slate-400 line-through' : 'text-slate-800'
              }`}
            >
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

/**
 * Coaching panel (Phase 3F). Read-only. The desktop processing pipeline
 * is the sole producer; the web never edits coaching notes. We surface
 * the summary verbatim plus the bullet list if present.
 *
 * Deliberately NOT shown:
 *   - A public comparison or per-dimension breakdown (we don't store one)
 *   - Agent attribution (we don't compute a rollup)
 *   - A "regenerate" button (desktop owns re-processing)
 */
function CoachingSection({ notes }: { notes: AdminCoachingNotes }) {
  const hasBullets = Array.isArray(notes.bullets) && notes.bullets.length > 0;
  return (
    <SectionCard className="mt-5 space-y-4 p-5">
      <SectionHeading
        title="Coaching"
        kicker="Read-only desktop coaching notes attached to this call."
        badge={notes.model ? <StatusBadge tone="purple">{notes.model}</StatusBadge> : <StatusBadge tone="purple">Available</StatusBadge>}
      />
      {notes.summary ? (
        <p className="whitespace-pre-wrap text-sm text-slate-700">{notes.summary}</p>
      ) : (
        <p className="text-xs text-slate-500">No summary. (Bullets only.)</p>
      )}
      {hasBullets && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
          {notes.bullets!.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/**
 * Lazy-loading transcript panel. The detail response only tells us
 * `hasTranscript: boolean`; the body itself is fetched the first time
 * the section is expanded. Transcripts can be ~1 MB — loading them
 * eagerly would make every detail-page open feel slow.
 *
 * Once fetched, the transcript stays in memory for the life of the
 * page (no re-fetch on collapse/expand). We do NOT persist across
 * route navigations — opening another call and coming back will
 * re-fetch. That's intentional; transcripts can be updated by a
 * re-processing pass on the desktop, and always-fresh is simpler
 * than cache invalidation.
 */
function TranscriptSection({ callSid }: { callSid: string }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<AdminCallTranscriptResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && !data && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch<AdminCallTranscriptResponse>(
          `/api/admin/calls/${encodeURIComponent(callSid)}/transcript`,
        );
        setData(res);
      } catch (err) {
        // A 404 here after hasTranscript=true should be impossible in
        // practice, but if it happens we show it plainly rather than
        // hiding the section.
        setError(
          err instanceof ApiError ? err.message : (err as Error).message || 'Failed to load transcript',
        );
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <SectionCard className="mt-5 overflow-hidden p-0">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-sm font-semibold text-slate-900">Transcript</span>
        <span className="flex items-center gap-2 text-xs text-slate-500">
          {data && <span>{data.wordCount.toLocaleString()} words</span>}
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4">
          {loading ? (
            <p className="text-sm text-slate-500">Loading transcript…</p>
          ) : error ? (
            <p className="text-sm text-red-700">{error}</p>
          ) : data ? (
            <TranscriptBody data={data} />
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}

function stateCardClass(tone: CallDetailStateCardTone): string {
  switch (tone) {
    case 'ready':
      return 'border-emerald-200 bg-emerald-50 text-emerald-900';
    case 'waiting':
      return 'border-amber-200 bg-amber-50 text-amber-900';
    case 'muted':
      return 'border-slate-200 bg-slate-50 text-slate-600';
  }
}

function TranscriptBody({ data }: { data: AdminCallTranscriptResponse }) {
  // Prefer segments when present: timestamps + speaker labels are far
  // more useful to a reviewing admin than a wall of text. Fall back
  // to the concatenated fullText when segments are absent (older
  // desktop builds, or a pipeline that couldn't diarize).
  const hasSegments = data.segments && data.segments.length > 0;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {data.model && <span>Model: <code className="text-slate-700">{data.model}</code></span>}
        <span>Captured {new Date(data.createdAt).toLocaleString()}</span>
      </div>

      {hasSegments ? (
        <ol className="space-y-2 text-sm">
          {data.segments!.map((seg, i) => (
            <TranscriptSegmentRow key={`${seg.startMs}-${i}`} seg={seg} />
          ))}
        </ol>
      ) : (
        // Preserve paragraph breaks and monospace timestamps a reviewer
        // might paste in; whitespace-pre-wrap keeps linebreaks honest.
        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800">
          {data.fullText}
        </pre>
      )}
    </>
  );
}

function TranscriptSegmentRow({ seg }: { seg: TranscriptSegment }) {
  return (
    <li className="flex gap-3 text-sm">
      <span className="w-16 shrink-0 font-mono text-xs text-slate-400 tabular-nums">
        {formatMs(seg.startMs)}
      </span>
      <div className="min-w-0 flex-1">
        {seg.speaker && (
          <span className="mr-2 text-xs font-semibold text-slate-500">
            {seg.speaker}
          </span>
        )}
        <span className="text-slate-800">{seg.text}</span>
      </div>
    </li>
  );
}

function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function shortSid(sid: string): string {
  if (sid.length <= 12) return sid;
  return `${sid.slice(0, 6)}…${sid.slice(-4)}`;
}

/**
 * Render a phone number, linking to the contact page only when this
 * phone is the external party (i.e., the CONTACT, not our own
 * business number). Our business numbers have a number_owner; they
 * don't have a contact page.
 */
function PhoneLink({ phone, linkThis }: { phone: string; linkThis: boolean }) {
  if (!linkThis) return <>{phone}</>;
  return (
    <Link
      to={buildContactDetailPath(phone)}
      className="hover:underline"
    >
      {phone}
    </Link>
  );
}
